import Phaser from "phaser";
import BackgroundScene from "./Common/BackgroundScene";
import CharacterAttributesGUI from "../GUI/CharacterAttributesGUI";
import CharacterAttributes from "../Character/CharacterAttributes";
import AttributeMath from "../Math/AttributeMath";
import CharacterStorage from "../Storage/CharacterStorage";
import StoryStorage from "../Storage/StoryStorage";
import ExperiencePoints from "../Character/ExperiencePoints";
import BackButton from "../GUI/BackButton";
import SkillAllocationGUI from "../GUI/SkillAllocationGUI";
import SkillManager from "../Skills/SkillManager";
import SkillStorage from "../Storage/SkillStorage";
import GameSettings from "../GameSettings";

export default class CharacterSkillsScene extends BackgroundScene {
  private mAttribGUI: CharacterAttributesGUI;
  private mSkillGUI: SkillAllocationGUI;
  private mCharacterStorage: CharacterStorage;
  private mCharAttribs: CharacterAttributes;
  private mExperiencePoints: ExperiencePoints;
  private mBackButton!: BackButton;

  constructor() {
    super(
      "characterBackgroundScene",
      "images/Scenes/CharacterScene.png",
      "CharacterSkillScene"
    );
    this.mCharacterStorage = new CharacterStorage();
  }

  public preload() {
    super.preload();

    this.mCharAttribs = new CharacterAttributes();

    this.mCharAttribs.Strength = this.mCharacterStorage.GetStrength();
    this.mCharAttribs.Vitality = this.mCharacterStorage.GetVitality();
    this.mCharAttribs.Dexterity = this.mCharacterStorage.GetDexterity();

    this.mCharAttribs.Intelligence = this.mCharacterStorage.GetIntelligence();

    this.mCharAttribs.AvailableAttributePoints =
      this.mCharacterStorage.GetAvailableAttributePoints();

    this.mCharAttribs.AvailableSkillPoints =
      this.mCharacterStorage.GetAvailableSkillPoints();

    this.mExperiencePoints = ExperiencePoints.create();

    this.mCharAttribs.MaxHealthPoints = AttributeMath.calculateMaxHP(
      this.mExperiencePoints.Level,
      this.mCharAttribs.Vitality
    );
    this.mCharAttribs.HealthPoints = this.mCharAttribs.MaxHealthPoints;

    this.mCharAttribs.MaxManaPoints = AttributeMath.calculateMaxMP(
      this.mExperiencePoints.Level,
      this.mCharAttribs.Intelligence
    );
    this.mCharAttribs.ManaPoints = this.mCharAttribs.MaxManaPoints;

    this.mAttribGUI = new CharacterAttributesGUI(this.mCharAttribs);
    this.mAttribGUI.preload(this);

    const skillStorage = new SkillStorage();
    const skillManager = new SkillManager(skillStorage);
    this.mSkillGUI = new SkillAllocationGUI(this.mCharAttribs, skillManager, this.mCharacterStorage);
    this.mSkillGUI.preload(this);

    BackButton.preload(this);
  }

  public create(): void {
    super.create();
    if (this.input.keyboard == null) {
      return;
    }

    this.input.keyboard.on("keydown", (event) => {
      if (event.code == "Esc") {
        this.scene.start("MainMenu");
      }
    });

    this.mAttribGUI.create(this, 50, 50);

    this.add.text(
      50, 340,
      `Max MP: ${this.mCharAttribs.MaxManaPoints}`,
      { fontFamily: "monospace", fontSize: "28px", color: "#0066FF" }
    );

    const nextThreshold = this.mExperiencePoints.GetNextLevelThreshold();
    const expProgressLabel = nextThreshold != null
      ? `${this.mExperiencePoints.ExperiencePoints} / ${nextThreshold} XP`
      : `${this.mExperiencePoints.ExperiencePoints} XP  MAX LEVEL`;
    this.add.text(
      50, 380,
      `Lv.${this.mExperiencePoints.Level}  ${expProgressLabel}`,
      { fontFamily: "monospace", fontSize: "28px", color: "#FFD700" }
    );

    this.mSkillGUI.create(this, 550, 50);

    if (GameSettings.debugMode) {
      this.buildDebugPanel();
    }

    this.mBackButton = new BackButton(this, "MainMenu"); // default bottom-left
  }

  private buildDebugPanel(): void {
    const panelY = 450;

    this.add.text(50, panelY, "[DEBUG]", {
      fontFamily: "monospace",
      fontSize: "16px",
      color: "#ff4444",
    });

    const lvlBtn = this.makeDebugButton(50, panelY + 28, "+Level");
    lvlBtn.on("pointerdown", () => {
      const next = this.mExperiencePoints.GetNextLevelThreshold();
      if (next === null) return;
      new StoryStorage().SetExp(next);
      this.scene.restart();
    });

    const attrBtn = this.makeDebugButton(160, panelY + 28, "+5 Attr Pts");
    attrBtn.on("pointerdown", () => {
      const cur = this.mCharacterStorage.GetAvailableAttributePoints();
      this.mCharacterStorage.SetAvailableAttributePoints(cur + 5);
      this.scene.restart();
    });

    const skillBtn = this.makeDebugButton(310, panelY + 28, "+5 Skill Pts");
    skillBtn.on("pointerdown", () => {
      const cur = this.mCharacterStorage.GetAvailableSkillPoints();
      this.mCharacterStorage.SetAvailableSkillPoints(cur + 5);
      this.scene.restart();
    });
  }

  private makeDebugButton(x: number, y: number, label: string): Phaser.GameObjects.Text {
    return this.add.text(x, y, label, {
      fontFamily: "monospace",
      fontSize: "18px",
      color: "#ffffff",
      backgroundColor: "#882200",
      padding: { x: 10, y: 6 },
    }).setInteractive({ useHandCursor: true });
  }

  public update(time: number, delta: number): void {
    if (this.input.keyboard == null) {
      return;
    }
    const esc = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    this.events.on("update", () => {
      if (Phaser.Input.Keyboard.JustDown(esc)) {
        this.scene.start("MainMenu");
      }
    });
  }
}
